import { REGEX_PATTERNS } from '@/app.constants';
import React from 'react';

// Restriction keys
const RESTRICTIONS_KEYS = {
  MAX_LENGTH: 'maxLength',
  ONLY_NUMBERS: 'onlyNumbers',
  DECIMAL: 'decimal',
} as const;

const removeZeroesInBeginning = (value: string) => {

  // Handle empty string
  if (!value) return value;

  // Handle case where value is just zeroes (0, 00, 000, etc.)
  if (REGEX_PATTERNS.ONLY_ZEROES.test(value)) return value;

  // Handle negative numbers
  if (value.startsWith('-')) {
    const afterMinus = value.substring(1);
    
    // If after minus is empty (just "-"), return as is to allow typing
    if (!afterMinus) return value;
    
    // If after minus is just zeroes, return original
    if (REGEX_PATTERNS.ONLY_ZEROES.test(afterMinus)) return value;
    
    // Handle decimal case for negative numbers (e.g., -0., -00. -> -0.)
    if (REGEX_PATTERNS.ONLY_ZEROES_WITH_DECIMAL.test(afterMinus)) return '-0.';
    
    // Handle decimal case with numbers after decimal for negative numbers (e.g., -0.2, -00.2 -> -0.2)
    if (REGEX_PATTERNS.ONLY_ZEROES_WITH_DECIMAL_AND_NUMBER.test(afterMinus)) return '-0' + afterMinus.replace(/^0+/, '');
    
    // Remove leading zeroes after minus sign
    const cleaned = afterMinus.replace(REGEX_PATTERNS.ONLY_ZEROES_WITH_LEADING_ZEROES, '');
    // If cleaned is empty (was just zeroes), return '-0'
    if (!cleaned) return '-0';
    return '-' + cleaned;
  }

  // Handle positive numbers
  // Handle decimal case (e.g., 0., 00. -> 0.)
  if (REGEX_PATTERNS.ONLY_ZEROES_WITH_DECIMAL.test(value)) return '0.';

  // Handle decimal case with numbers after decimal (e.g., 0.2, 00.2 -> 0.2)
  if (REGEX_PATTERNS.ONLY_ZEROES_WITH_DECIMAL_AND_NUMBER.test(value)) return '0' + value.replace(/^0+/, '');
  
  // Remove leading zeroes
  const cleaned = value.replace(REGEX_PATTERNS.ONLY_ZEROES_WITH_LEADING_ZEROES, '');
  // If cleaned is empty (was just zeroes), return '0'
  if (!cleaned) return '0';
  return cleaned;
};

const removeTrailingOrIntermediateMinus = (value: string) => {
  if (value.match(REGEX_PATTERNS.ONLY_ZEROES_WITH_LEADING_ZEROES_AND_NUMBER)) {
    return value.replace(/-+/g, '').replace(/^-/g, '');
  }
  return value;
};

// Restriction handler definitions
const RESTRICTIONS = {
  [RESTRICTIONS_KEYS.MAX_LENGTH]: {
    handler: ({ max }: { max: number }) => (value: string) => value.slice(0, max),
    param: ['max'] as const,
  },
  [RESTRICTIONS_KEYS.ONLY_NUMBERS]: {
    handler: ({ allowNegative }: { allowNegative: boolean }) => (value: string) => {
      let sanitized = allowNegative ? value.replace(/[^\d-]/g, '').replace(/^-+/g, '-') : value.replace(/[^\d]/g, '');
      sanitized = removeTrailingOrIntermediateMinus(sanitized);
      return removeZeroesInBeginning(sanitized);
    },
    param: ['allowNegative'] as const,
  },
  [RESTRICTIONS_KEYS.DECIMAL]: {
    handler: ({ upto, allowNegative }: { upto: number, allowNegative: boolean }) => (value: string) => {
      // Remove all non-digit and non-dot characters, allow only single negative sign, and also there should not be any - after the number
      let sanitized = allowNegative ? value.replace(/[^\d.-]/g, '').replace(/^-+/g, '-') : value.replace(/[^\d.]/g, '');
      // Only allow the first dot, remove any additional dots
      const firstDotIdx = sanitized.indexOf('.');
      if (firstDotIdx !== -1) {
        sanitized =
          sanitized.slice(0, firstDotIdx + 1) +
          sanitized.slice(firstDotIdx + 1).replace(/\./g, '');
      }
      // Split into integer and decimal parts
      const [integerPart, decimalPart] = sanitized.split('.');
      if (typeof upto === 'number' && sanitized.includes('.')) {
        // Limit decimal part to 'upto' digits, and also allow only numbers in decimal parts
        const limitedDecimal = (decimalPart || '').slice(0, upto).replace(/[^0-9]/g, '');
        sanitized = integerPart + '.' + limitedDecimal;
      }
      // remove all the trailing - from the number if there is any number before it
      sanitized = removeTrailingOrIntermediateMinus(sanitized);
      return removeZeroesInBeginning(sanitized);
    },
    param: ['upto', 'allowNegative'] as const,
  }
} as const;

type RestrictionConfig =
  | { name: typeof RESTRICTIONS_KEYS.MAX_LENGTH; max: number }
  | { name: typeof RESTRICTIONS_KEYS.ONLY_NUMBERS }
  | { name: typeof RESTRICTIONS_KEYS.DECIMAL }

type Props = {
  restrictions: RestrictionConfig[];
};

/**
 * useRestriction hook applies built-in input restrictions based on the array of restriction configs.
 *
 * Example usage:
 *   const { handleOnInput } = useRestriction({
 *     restrictions: [
 *       { name: RESTRICTIONS_KEYS.MAX_LENGTH, max: 10 },
 *       { name: RESTRICTIONS_KEYS.ONLY_NUMBERS, allowNegative: true },
 *       { name: RESTRICTIONS_KEYS.DECIMAL, upto: 2, allowNegative: true },
 *     ]
 *   });
 *   <input onInput={handleOnInput} />
 */
const useRestriction = (props: Props) => {
  const { restrictions = [] } = props;

  const handleOnInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    let modified = false;

    for (const restriction of restrictions) {
      const restrictionDef = RESTRICTIONS[restriction.name];
      if (!restrictionDef) continue;
      if (restrictionDef.param.length > 0) {
        // Handler expects params
        type HandlerWithParams = (...args: unknown[]) => (v: string) => string;
        // convert param into an object with the same name as the param, use reduce for that
        const params = restrictionDef.param.reduce((acc, paramName) => {
          acc[paramName] = (restriction as Record<string, unknown>)[paramName];
          return acc;
        }, {} as Record<string, unknown>);

        value = (restrictionDef.handler as HandlerWithParams)(params)(value);
        modified = true;
      } else {
        // Handler expects no params
        type HandlerNoParams = () => (v: string) => string;
        value = (restrictionDef.handler as HandlerNoParams)()(value);
        modified = true;
      }
    }

    if (modified) {
      e.target.value = value;
    }
  };

  return { handleOnInput };
};

export { RESTRICTIONS, RESTRICTIONS_KEYS };
export default useRestriction;